import { MxAction, ActionContextType, ActionPurpose, ActionsContext, ActionDeviceVisibility, ActionUiZone } from '../model/action';
import type { DeviceService } from '../../../App/services/device-service';
import type { MxApplicationService } from '../../../App/services/mx-application-service';
import type { I18n } from '../../../i18n/mx-internationalization';
import type { UserProfileService } from '../../../App/services/user-profile-service';
import type { SchemaService } from '../../PandoraSolutionBuilderAgent/services/schema-service';
import type { RoutingService } from '../../../App/services/mx-routing-service';
import DetailsView from '../model/details-view';
import { EntityContext } from '../../PandoraSolutionBuilderAgent/services/entity-service';
import { ContentWidgetDialogAction } from '../../PandoraSolutionBuilderAgent/model/enumerations';
import { ActionFilterType } from '../model/action';
import _ from '../../../Utils/mx-lodash';
import { utils } from '../../../Utils/mx-utils';
import { ClassType } from '@dwp/api-parser/dist/api/Schema/classes/Schema/Classes.GetClass';

type ActionAvailabilityInfo = {
	isAvailable: boolean;
	cacheId?: string;
};

export interface ActionProvider {
	getActions: (forceReload?: boolean) => Promise<MxAction[]>;
	checkAvailability: (action: MxAction, selectedObjects: EntityContext[]) => Promise<ActionAvailabilityInfo>;
}

class DefaultActionProvider implements ActionProvider {
	async getActions() {
		return [];
	}
	async checkAvailability() {
		return { isAvailable: true };
	}
}

/**
 * @ngdoc service
 * @name mx.shell.ActionManagerService
 * @module mx.shell
 *
 * @description
 * A service provides methods for actions managing, subscribing and notifying about changes in active actions.
 * The service analyze the state of the environment, like System Context, the definition of the current view's
 * {@ref mx.shell.Object Action Context}, calculates the available Actions, and throws a notification event to notify
 * subscribers about active Actions list change<br>
 * <h3>Action manager visualization</h3>
 * <img src="https://github.com/Matrix42AG/Pandora/blob/dev/docs/actions-manager.png?raw=true" alt="Action manager schema"><img>
 */
export class MxActionManagerService {
	private actionProvider: ActionProvider = new DefaultActionProvider();

	static $inject = [
		'mx.app.DeviceService',
		'mx.shell.AppsService',
		'mx.internationalization',
		'mx.shell.UserProfileService',
		'mx.SolutionBuilderAgent.SchemaService',
		'mx.shell.RoutingService',
	];
	constructor(
		private readonly deviceService: DeviceService,
		private readonly applicationService: MxApplicationService,
		private readonly i18n: I18n,
		private readonly userProfileService: UserProfileService,
		private readonly schemaService: SchemaService,
		private readonly routingService: RoutingService
	) {}

	public async checkAvailability(action: MxAction, selectedObjects: EntityContext[]): Promise<ActionAvailabilityInfo> {
		let isAvailable = true;
		if (Array.isArray(action.applications) && action.applications.length > 0) {
			const application = await this.applicationService.getCurrentApplication();
			isAvailable = action.applications.indexOf(application.value) >= 0;
		}

		if (!isAvailable) {
			return { isAvailable: false };
		}

		if (!action || !action.id || action.isService || selectedObjects.length === 0) {
			return { isAvailable: true };
		}

		return this.actionProvider.checkAvailability(action, selectedObjects);
	}

	/**
	 * @ngdoc method
	 * @name mx.shell.ActionManagerService#setActionProvider
	 *
	 * @description
	 * Set an instance of {@ref mx.shell.ActionProviderService an ActionProviderService} which will be sued for getActions method
	 *
	 * @param {ActionProvider?} actionProvider - an instance of ActionProvider
	 */
	public setActionProvider(actionProvider?: ActionProvider): void {
		if (actionProvider && typeof actionProvider.getActions === 'function') {
			this.actionProvider = actionProvider;
		}
	}

	/**
	 * @ngdoc method
	 * @name mx.shell.ActionManagerService#filterActions
	 *
	 * @description
	 * Returns a list of Actions which were filtered inside the function. By Default same result as input is returned. However, this can be overridden on client
	 *
	 * @param {mx.shell.Action[]} actions - Array of actions to be filtered {@ref mx.shell.Action actions}
	 * @param {DetailsView} view - A {@ref mx.shell.DetailsView view} to filter actions for
	 * @param {object[]} selectedObjects - Array of selected objects to filter actions for
	 *
	 * @returns {mx.shell.Action[]} Array of available {@ref mx.shell.Action actions}
	 */
	public filterActions(actions: MxAction[], view?: DetailsView, selectedObjects?: EntityContext[]): MxAction[] {
		const route = (this.routingService.getCurrent() || {}).parameters;
		const filterExecutionContext = route
			? {
					navigationId: (route.navigationId || '').toLowerCase(),
					filterId: (route.filterId || '').toLowerCase(),
					application: route.appString || '',
			  }
			: {};

		return (
			actions
				.filter((action) => {
					if (selectedObjects && selectedObjects[0]?._archived) {
						const isEditAction = action.name.indexOf('ActionEdit') > -1 || action.name === 'AdvancedDetails';
						if (!isEditAction) {
							return false;
						}
					}

					if (!view && !selectedObjects) {
						return action.scriptFilter ? this.actionsFilterScript(undefined, action.scriptFilter, 0, filterExecutionContext) : true;
					}
					const viewForService =
						(view && view.type && view.type.startsWith('service_')) ||
						(selectedObjects?.length && selectedObjects[0]._type && selectedObjects[0]._type.indexOf('service_') === 0);

					if (action.isService !== undefined && viewForService !== action.isService) {
						//for NME a !== b  ===  a XOR b (if a, b are boolean)
						return false;
					}

					let scriptPredicate = true;
					if (action.scriptFilter) {
						selectedObjects?.forEach((selectedObject) => {
							if (scriptPredicate) {
								const result = this.actionsFilterScript(selectedObject, action.scriptFilter as string, selectedObjects.length, filterExecutionContext);
								if (typeof result === 'boolean') {
									scriptPredicate = result;
								}
							}
						});
					}
					const viewType = view?.dialogType || ContentWidgetDialogAction.Preview;
					if (viewType === ContentWidgetDialogAction.Edit && action.uiZones && action.uiZones.indexOf(ActionUiZone.EditDialog) !== -1) {
						return scriptPredicate;
					}

					return scriptPredicate && viewType === ContentWidgetDialogAction.Preview;
				})
				// First - items without position, then - sort by `position`, then - by `title`
				.sort(function (a, b) {
					let res = 0,
						aTitle,
						bTitle;
					if (typeof a.position === 'undefined' && typeof b.position !== 'undefined') {
						res = -1;
					} else if (typeof a.position !== 'undefined' && typeof b.position === 'undefined') {
						res = 1;
					} else if ((a.position || 0) !== (b.position || 0)) {
						res = (a.position || 0) > (b.position || 0) ? 1 : -1;
					} else if (a.title && b.title && a.title !== b.title) {
						aTitle = (a.title + '').trim().toLowerCase();
						bTitle = (b.title + '').trim().toLowerCase();
						res = aTitle > bTitle ? 1 : -1;
					}
					return res;
				})
		);
	}

	/**
	 * @ngdoc method
	 * @name mx.shell.ActionManagerService#getActions
	 *
	 * @description
	 * Returns a list of Actions which fits the specified context
	 *
	 * @param {ActionsContext} context - Specifies the {@ref mx.shell.ActionsContext context} actions will be taken for. Can be an array of types.
	 * @param {ActionContextType[]} actionTypes - Specifies the list of {@ref mx.shell.ActionContextType context types} to be got. All types will be taken if empty or null.
	 * @param {EntityContext[]} objects - Specifies an array of selected {@ref mx.shell.Object objects}. Actions will be filtered according their context.
	 * @param {ActionPurpose[]} purposes - Specifies an array of purposes. Or null if filter by purposes is not needed
	 *
	 * @returns {mx.shell.Action[]} Array of available {@ref mx.shell.Action actions}
	 */
	public async getActions(context: ActionsContext, actionTypes?: ActionContextType[], objects?: EntityContext[], purposes?: ActionPurpose[]): Promise<MxAction[]> {
		let actions = await this.requestActions(context);
		actions = await this._filterActions(
			actions, // Array of found actions
			context.uiZone, // E.g:  mx.shell.ActionUiZone.Toolbar
			actionTypes, // E.g:  [mx.shell.ActionContextType.Single, mx.shell.ActionContextType.Multiple]
			objects, // Array of connected  objects
			purposes
		);
		return this._sortActions(actions);
	}

	/**
	 * @ngdoc method
	 * @name mx.shell.ActionManagerService#getAllActions
	 *
	 * @description
	 * Gets the list of all actions {@ref mx.shell.Action Actions} in system.<br>
	 *
	 * @returns {mx.shell.Action[]} Array of all actions {@ref mx.shell.Action actions} in system.
	 */
	async getAllActions(): Promise<MxAction[]> {
		return await this.actionProvider.getActions();
	}

	public async getActionById(id: string): Promise<MxAction | null> {
		if (!id) {
			return null;
		}
		const actions = await this.getAllActions();
		return actions.find((action) => action.id === id) || null;
	}

	public async getActionsByIds(ids: string[]): Promise<MxAction[]> {
		if (!Array.isArray(ids)) {
			return [];
		}
		const actions = await this.getAllActions();
		return actions.filter((action) => action.id && ids.includes(action.id));
	}

	private async requestActions(context: ActionsContext): Promise<MxAction[]> {
		if (!context) {
			throw new Error(this.i18n.get('shell.errors.noContextForRequestActions'));
		}

		if (context.hideActions) {
			return [];
		}

		const resActions = (await this.actionProvider.getActions()).filter((action) => {
			// TODO: move it to method "_filterActions"
			const typesFilter = action.typesFilter || ActionFilterType.Any;
			let res = false;

			switch (typesFilter) {
				case ActionFilterType.Any:
					res = true;
					break;
				case ActionFilterType.Specified:
					res = _.intersection(action.availableTypes, context.types).length > 0;
					break;
				case ActionFilterType.ExceptSpecified:
					res = _.intersection(action.availableTypes, context.types).length === 0;
					break;
			}
			return res;
		});

		return (context.extraActions || []).concat(resActions);
	}

	private _filterActionsByUiZone(actions: MxAction[], uiZone?: ActionUiZone): MxAction[] {
		if (typeof uiZone !== 'undefined' && this._isUiZoneExists(uiZone)) {
			actions = actions.filter((action) => !action.uiZones || action.uiZones.includes(uiZone));
		}
		return actions;
	}

	private _getTypesAndStates(objects: EntityContext[]): { objectTypes: string[]; objectStates: string[] } {
		const objectTypes: string[] = [];
		const objectStates: string[] = [];

		objects.forEach((object) => {
			if (object._type && !objectTypes.includes(object._type)) {
				objectTypes.push(object._type);
			}
			if (typeof object._state !== 'undefined' && !objectStates.includes(object._state)) {
				objectStates.push(object._state);
			}
		});

		return { objectTypes, objectStates };
	}

	private _sortActions(actions: MxAction[]): MxAction[] {
		if (actions && actions.length) {
			return actions.sort((action1, action2) => {
				const pos1 = action1.position || 0;
				const pos2 = action2.position || 0;

				if (pos2 !== pos1) {
					// Descending sorting by position
					return pos2 - pos1;
				}

				return action1.title.localeCompare(action2.title);
			});
		}

		return [];
	}

	private async _filterActions(
		actions: MxAction[],
		uiZone?: ActionUiZone,
		actionTypes?: ActionContextType[],
		objects: EntityContext[] = [],
		purposes?: ActionPurpose[]
	): Promise<MxAction[]> {
		if (!actions || !actions.length) {
			return [];
		}

		const app = await this.applicationService.getCurrentApplication();
		if (app?.value) {
			actions = actions.filter((action) => !action.applications || action.applications.includes(app.value));
		}
		actions = this._filterActionsByUiZone(actions, uiZone);

		actions = actions.filter((action) => {
			switch (action.deviceVisibility) {
				case ActionDeviceVisibility.DesktopOnly:
					if (!this.deviceService.screenDesktop()) {
						return false;
					}
					break;
				case ActionDeviceVisibility.MobileOnly:
					if (!this.deviceService.screenMobile()) {
						return false;
					}
					break;
			}
			return uiZone === ActionUiZone.SearchPage || !actionTypes || actionTypes.includes(action.contextType);
		});

		if (Array.isArray(purposes)) {
			actions = actions.filter((action) => !action.purpose || purposes.includes(action.purpose));
		}

		const { objectTypes, objectStates } = this._getTypesAndStates(objects);

		actions = this._filterActionsByObjectType(actions, objectTypes);

		return actions.filter((action, index, actionsArray) => {
			// Filter duplicates
			if (this._isDuplicate(actionsArray.slice(0, index), action)) {
				return false;
			}

			// Filter by context type
			if (uiZone === ActionUiZone.SearchPage || action.contextType === ActionContextType.None) {
				return true;
			}

			if (objects.length === 0 || (action.contextType === ActionContextType.Single && objects.length !== 1)) {
				return false;
			}

			// Filter by state
			if (action.statesFilter) {
				if (action.statesFilter === ActionFilterType.Specified) {
					if (!action.states.length || objectStates.length === 0 || _.intersection(objectStates, action.states).length !== objectStates.length) {
						return false;
					}
				} else if (action.statesFilter === ActionFilterType.ExceptSpecified) {
					if (objectStates.length > 0 && action.states && action.states.length && _.intersection(objectStates, action.states).length > 0) {
						return false;
					}
				}
			}

			return true;
		});
	}

	private _filterActionsByObjectType(actions: MxAction[], objectTypes: string[]): MxAction[] {
		return actions.filter((action) => {
			switch (action.typesFilter) {
				case ActionFilterType.Specified:
					if (!action.availableTypes) {
						return false;
					}

					return objectTypes.every((type) => action.availableTypes.includes(type));

				case ActionFilterType.ExceptSpecified:
					if (!action.availableTypes) {
						return true;
					}

					return objectTypes.every((type) => !action.availableTypes.includes(type));
			}

			return true;
		});
	}

	private _isDuplicate(actions: MxAction[], action: MxAction): boolean {
		return actions.some((item) => item.name === action.name);
	}

	private _isUiZoneExists(uiZone: ActionUiZone): boolean {
		return Object.values(ActionUiZone).includes(uiZone);
	}

	public async getCreateActions(type: string): Promise<MxAction[]> {
		const context = new ActionsContext([type], [], ActionUiZone.SearchPage);
		const actions = await this.getActions(context, [ActionContextType.None], [], [ActionPurpose.Create]);
		return this.filterActions(actions);
	}

	public async getEditActions(object: EntityContext): Promise<MxAction[]> {
		const context = new ActionsContext([object._type], [], ActionUiZone.SidePanel);
		const actionTypes = [ActionContextType.Single, ActionContextType.Multiple];
		const purposes = [ActionPurpose.Edit];

		const actions = await this.getActions(context, actionTypes, [object], purposes);
		return this.filterActions(actions, undefined, [object]);
	}

	public async isExportAllAvailable(types?: string[], className?: string): Promise<boolean> {
		const entities = await this.getTypes(types, className);
		const actionContext = new ActionsContext(entities, [], undefined);
		const actions = await this.getActions(actionContext, [ActionContextType.None], []);
		return actions.some((action) => action.name.startsWith('ExportAll'));
	}

	private async getTypes(types?: string[], className?: string): Promise<string[]> {
		if (Array.isArray(types) && types.length > 0) {
			return types;
		} else if (typeof className !== 'string') {
			return [];
		} else {
			const classInfo = await this.schemaService.getClassInfo(className);
			return classInfo.ClassType === ClassType.CommonClass ? [] : classInfo.UsedInTypes.map((typeInfo) => typeInfo.InternalName || '');
		}
	}

	private actionsFilterScript(item: unknown, script: string, count = 0, filterContext: { navigationId?: string; filterId?: string; application?: string }) {
		try {
			const safeScript = utils.getSafeScript(script);

			const func = new Function('$object', '$currentUser', '$count', '$context', 'return ' + safeScript);
			return func(item, this.userProfileService.id, count, filterContext);
		} catch (e) {
			console.error((e as Error).message);
			return false;
		}
	}
}
